import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { extractErrorMessage } from '@/lib/errors';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  employmentTypesKeys,
  useCreateEmploymentType,
  useDeleteEmploymentType,
  useEmploymentTypes,
  useUpdateEmploymentType,
} from '@/hooks/use-organizations';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import type { EmploymentTypeWithCount } from '@/types/employee';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AlertCircle, Briefcase, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { SettingsSectionHeader } from '@/components/layout/settings-section-header';
import { useState } from 'react';
import { toast } from 'sonner';

const routePath = '/settings/employment-types' as const;
export const Route = createFileRoute(routePath)({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'employment-types' } });
  },
});

interface FormData {
  name: string;
  description: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
};

// Toggle active mutation
async function toggleEmploymentTypeActive(id: string): Promise<EmploymentTypeWithCount> {
  const { data } = await api.put<EmploymentTypeWithCount>(`/employment-types/${id}/toggle-active`);
  return data;
}

function useToggleEmploymentTypeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleEmploymentTypeActive,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: employmentTypesKeys.all });
      const previousTypes = queryClient.getQueryData(employmentTypesKeys.list());

      queryClient.setQueryData(
        [...employmentTypesKeys.list(), { includeInactive: true }],
        (old: EmploymentTypeWithCount[] | undefined) =>
          old?.map((type) => (type.id === id ? { ...type, isActive: !type.isActive } : type))
      );

      return { previousTypes };
    },
    onError: (err, _id, context) => {
      if (context?.previousTypes) {
        queryClient.setQueryData(employmentTypesKeys.list(), context.previousTypes);
      }
      toast.error(extractErrorMessage(err, 'Failed to update employment type status'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypesKeys.all });
    },
  });
}

export function EmploymentTypesPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<EmploymentTypeWithCount | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<{ name?: string; server?: string }>({});

  const { data: employmentTypes = [], isLoading, error } = useEmploymentTypes(true);
  const createMutation = useCreateEmploymentType();
  const updateMutation = useUpdateEmploymentType();
  const deleteMutation = useDeleteEmploymentType();
  const toggleActiveMutation = useToggleEmploymentTypeActive();

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  const handleCreate = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleEdit = (type: EmploymentTypeWithCount) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
    });
    setErrors({});
    setEditDialogOpen(true);
  };

  const handleDelete = (type: EmploymentTypeWithCount) => {
    setSelectedType(type);
    setDeleteDialogOpen(true);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }

    try {
      await createMutation.mutateAsync({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      });
      setCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      setErrors({
        server: extractErrorMessage(err, 'Failed to create employment type'),
      });
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType || !formData.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: selectedType.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      });
      setEditDialogOpen(false);
      setSelectedType(null);
      resetForm();
    } catch (err) {
      setErrors({
        server: extractErrorMessage(err, 'Failed to update employment type'),
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedType) return;

    try {
      await deleteMutation.mutateAsync(selectedType.id);
      setDeleteDialogOpen(false);
      setSelectedType(null);
    } catch {
      // Failure is surfaced by the deleteMutation.isError block in the dialog below.
    }
  };

  const handleDialogClose = (setter: (open: boolean) => void) => (open: boolean) => {
    if (!open) resetForm();
    setter(open);
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-4xl space-y-4 p-6">
        <SettingsSectionHeader
          title="Employment Types"
          description="Manage employment types for your organization"
          actions={
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    className="h-9 w-9 shrink-0 cursor-pointer rounded-full"
                    onClick={handleCreate}
                    aria-label="Add employment type"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add employment type</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />

        {/* Employment Types List */}
        {error ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-destructive/10 p-3 mb-4">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-sm text-destructive mb-4">
                {error instanceof Error ? error.message : 'Failed to load employment types'}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="gap-2 cursor-pointer"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="h-5 w-10 bg-muted rounded-full" />
                  <div className="h-10 w-10 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-3 w-48 bg-muted rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : employmentTypes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Briefcase className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-base font-medium text-foreground mb-1">
                No employment types yet
              </h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                Create employment types to categorize your employees (e.g., Full-time, Part-time,
                Contractor)
              </p>
              <Button onClick={handleCreate} className="gap-2 cursor-pointer">
                <Plus className="h-4 w-4" />
                Create First Type
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {employmentTypes.map((type) => (
              <EmploymentTypeCard
                key={type.id}
                type={type}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleActive={() => toggleActiveMutation.mutate(type.id)}
                isToggling={
                  toggleActiveMutation.isPending && toggleActiveMutation.variables === type.id
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={handleDialogClose(setCreateDialogOpen)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Employment Type</DialogTitle>
            <DialogDescription>Add a new employment type for your organization.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSubmit} className="space-y-4">
            {errors.server && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {errors.server}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="create-name" className="text-xs font-medium text-muted-foreground">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="create-name"
                placeholder="e.g., Full-time, Part-time, Contractor"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({});
                }}
                disabled={createMutation.isPending}
                className={cn('h-9', errors.name && 'border-destructive')}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="create-description"
                className="text-xs font-medium text-muted-foreground"
              >
                Description
              </Label>
              <Input
                id="create-description"
                placeholder="Optional description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                disabled={createMutation.isPending}
                className="h-9"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateDialogOpen(false)}
                disabled={createMutation.isPending}
                className="cursor-pointer"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending} className="cursor-pointer">
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={handleDialogClose(setEditDialogOpen)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Employment Type</DialogTitle>
            <DialogDescription>Update the employment type details.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4">
            {errors.server && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {errors.server}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="edit-name" className="text-xs font-medium text-muted-foreground">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-name"
                placeholder="e.g., Full-time, Part-time, Contractor"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({});
                }}
                disabled={updateMutation.isPending}
                className={cn('h-9', errors.name && 'border-destructive')}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="edit-description"
                className="text-xs font-medium text-muted-foreground"
              >
                Description
              </Label>
              <Input
                id="edit-description"
                placeholder="Optional description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                disabled={updateMutation.isPending}
                className="h-9"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditDialogOpen(false)}
                disabled={updateMutation.isPending}
                className="cursor-pointer"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending} className="cursor-pointer">
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employment Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedType?.name}&quot;? This will deactivate
              the employment type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(selectedType?._count?.employees || 0) > 0 && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
              <p className="font-medium">Cannot delete</p>
              <p className="mt-1 text-xs">
                This type has {selectedType?._count?.employees} employee(s) assigned. Please
                reassign them before deleting.
              </p>
            </div>
          )}
          {deleteMutation.isError && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              <p className="font-medium">Failed to delete</p>
              <p className="mt-1 text-xs">
                {extractErrorMessage(deleteMutation.error, 'An unexpected error occurred.')}
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending} className="cursor-pointer">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={deleteMutation.isPending || (selectedType?._count?.employees || 0) > 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Employment Type Card Component
interface EmploymentTypeCardProps {
  type: EmploymentTypeWithCount;
  onEdit: (type: EmploymentTypeWithCount) => void;
  onDelete: (type: EmploymentTypeWithCount) => void;
  onToggleActive: () => void;
  isToggling: boolean;
}

function EmploymentTypeCard({
  type,
  onEdit,
  onDelete,
  onToggleActive,
  isToggling,
}: EmploymentTypeCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isActive = type.isActive ?? true;
  const employeeCount = type._count?.employees || 0;

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        isHovered && 'shadow-md',
        !isActive && 'opacity-60'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="flex items-center gap-4 py-4">
        {/* Active toggle */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center">
                <Switch
                  checked={isActive}
                  onCheckedChange={onToggleActive}
                  disabled={isToggling}
                  className="scale-75"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isActive ? 'Active' : 'Inactive'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Icon */}
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hygge/10">
          <Briefcase className="h-5 w-5 text-hygge" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className={cn('font-medium text-sm truncate', !isActive && 'text-muted-foreground')}>
            {type.name}
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {type.description || `${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Employee count badge */}
        <div className="hidden sm:flex items-center">
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
            {employeeCount} employee{employeeCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Actions - visible on hover */}
        <div
          className={cn(
            'flex items-center gap-1 transition-opacity duration-200',
            isHovered ? 'opacity-100' : 'opacity-0'
          )}
        >
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                  onClick={() => onEdit(type)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive cursor-pointer"
                  onClick={() => onDelete(type)}
                  disabled={employeeCount > 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{employeeCount > 0 ? 'Cannot delete - has employees' : 'Delete'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
